import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { User } from '@supabase/supabase-js'
import { useSettingsStore } from '@/stores/settings'
import { getSupabase, resetSupabaseClient } from './supabase'
import { deriveKey, fromBase64, generateSalt, importKey, toBase64 } from '@/utils/crypto'

/**
 * Store de autenticación con Supabase.
 *
 * Mantiene la sesión reactiva. Como el cliente Supabase se construye
 * desde los settings de sync, todas las operaciones verifican que
 * exista configuración; si no, devuelven error controlado.
 *
 * La sesión se persiste automáticamente por el SDK en localStorage
 * (storageKey 'epub-unlimited-auth'), así que sobrevive recargas.
 *
 * Además de la sesión, gestiona la `CryptoKey` usada para la sync
 * cifrada de la config IA (derivada de la contraseña del usuario).
 * La clave vive sólo en memoria: tras una recarga en frío la sesión
 * se restaura pero la clave no — el usuario debe desbloquearla de
 * nuevo con `unlock(password)` (flujo perezoso).
 */

/** Fila de `user_settings` (sólo lectura del salt; el resto es opaco). */
type UserSettingsRow = {
  salt: string | null
  updated_at: string | null
} | null

/**
 * Fila de `public.profiles`. Refleja el rol global del usuario y su
 * estado de suspensión. Se carga al iniciar sesión y se cachea aquí.
 */
type ProfileRow = {
  id: string
  email: string
  role: 'user' | 'admin'
  display_name: string | null
  created_at: string
  last_seen_at: string | null
  banned: boolean
  avatar_url: string | null
  bio: string | null
  is_public: boolean
} | null

/**
 * Clave bajo la que se guardan temporalmente los BYTES de la CryptoKey
 * (derivada por PBKDF2 de la contraseña) en sessionStorage para
 * auto-desbloquear la config IA tras una recarga dentro de la misma
 * pestaña. NUNCA se guarda la contraseña en claro: quien robe estos bytes
 * sólo puede descifrar la config IA de este usuario, no reutilizar su
 * contraseña de cuenta. sessionStorage se borra al cerrar la pestaña.
 */
const SESSION_KEY_BACKUP = 'epub-unlimited-unlock-key'
/** Clave legacy (contraseña en claro) que limpiamos si queda de versiones anteriores. */
const SESSION_PASSWORD_KEY_LEGACY = 'epub-unlimited-unlock-pw'

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  /**
   * True mientras `init()` está restaurando la sesión al arrancar la app.
   * La UI lo usa para mostrar un splash/loading en vez de flashear
   * "no autenticado" antes de que el SDK confirme la sesión.
   */
  const initializing = ref(false)

  /**
   * Perfil del usuario actual (tabla `public.profiles`). `null` hasta que
   * se carga (o si la instancia no tiene la tabla — la app sigue
   * funcionando, simplemente isAdmin queda en false).
   */
  const profile = ref<ProfileRow>(null)

  /**
   * CryptoKey AES-GCM derivada de la contraseña del usuario. `null`
   * cuando no hay sesión o la clave no se ha derivado todavía (recarga
   * en frío). NUNCA se persiste: vive sólo en memoria.
   */
  const encryptionKey = ref<CryptoKey | null>(null)
  /** Salt PBKDF2 del usuario actual (leído de la nube o generado). */
  const saltB64 = ref<string | null>(null)
  const unlocking = ref(false)

  /** ID del usuario actual (para nombrar canales/rooms). */
  const userId = computed(() => user.value?.id ?? null)
  const isAuthenticated = computed(() => user.value !== null)
  /** ¿Está la clave de cifrado disponible en memoria? */
  const isKeyUnlocked = computed(() => encryptionKey.value !== null)

  /** ¿Es administrador? False si no hay perfil o la tabla no existe. */
  const isAdmin = computed(() => profile.value?.role === 'admin' && !profile.value.banned)
  /** ¿Está suspendido? La UI usa esto para mostrar la pantalla de cuenta suspendida. */
  const isBanned = computed(() => profile.value?.banned === true)

  /**
   * Carga la fila de `public.profiles` del usuario actual. No lanza: si la
   * tabla no existe (instancia sin migrar) o hay error de red, deja
   * `profile` en null y la app sigue en modo single-user.
   */
  async function loadProfile(): Promise<void> {
    if (!user.value) {
      profile.value = null
      return
    }
    const c = client()
    if (!c) return
    try {
      const { data, error: err } = await c
        .from('profiles')
        .select('id, email, role, display_name, created_at, last_seen_at, banned, avatar_url, bio, is_public')
        .eq('id', user.value.id)
        .maybeSingle()
      if (err) throw err
      profile.value = data as ProfileRow
    } catch {
      // Tabla inexistente u otro error: modo degradado (sin admin/ban).
      profile.value = null
    }
  }

  /**
   * Actualiza los campos editables del propio perfil (F2.3):
   * `display_name`, `avatar_url`, `bio`, `is_public`. La RLS permite al
   * usuario tocar sus columnas no sensibles (no `role`/`banned`). Tras
   * actualizar, refresca el `profile` local para que la UI reaccione.
   */
  async function updateProfile(fields: {
    display_name?: string | null
    avatar_url?: string | null
    bio?: string | null
    is_public?: boolean
  }): Promise<void> {
    if (!user.value) throw new Error('No hay sesión activa')
    const c = client()
    if (!c) throw new Error('Sync no configurado')
    const { error: err } = await c
      .from('profiles')
      .update(fields)
      .eq('id', user.value.id)
    if (err) throw err
    // Refrescar local.
    await loadProfile()
  }

  /**
   * Heartbeat: actualiza `last_seen_at` del usuario. Best-effort, no lanza.
   * Lo llama un intervalo global para que el panel admin pueda calcular
   * DAU/WAU/MAU basándose en "última vez visto".
   */
  async function touchLastSeen(): Promise<void> {
    if (!user.value) return
    const c = client()
    if (!c) return
    try {
      await c.rpc('touch_last_seen')
    } catch {
      // silencioso: el heartbeat no debe romper la UX
    }
  }

  /** Cliente Supabase para la config actual, o null si falta config. */
  function client() {
    const settings = useSettingsStore()
    return getSupabase(settings.settings.sync)
  }

  /**
   * Restaura la sesión guardada al arrancar la app. No lanza: si no hay
   * cliente configurado o no hay sesión, simplemente deja user en null.
   *
   * NOTA: tras init() la sesión puede estar activa pero la
   * `encryptionKey` no — el usuario debe llamar a `unlock(password)`.
   */
  async function init() {
    const c = client()
    if (!c) return
    initializing.value = true
    try {
      const { data } = await c.auth.getSession()
      user.value = data.session?.user ?? null
      // Cargar el perfil en cuanto haya sesión (para isAdmin/isBanned).
      if (user.value) {
        await loadProfile()
        void touchLastSeen()
        // Auto-unlock: si hay clave respaldada en sessionStorage (guardada
        // al signIn/unlock anterior en esta pestaña), restaurarla sin
        // pedirle nada al usuario. Esto permite que la sync de config IA
        // funcione tras un F5 sin re-desbloqueo manual.
        // Limpieza legacy: versiones anteriores guardaban la contraseña en
        // claro; eliminarla si aparece.
        try {
          sessionStorage.removeItem(SESSION_PASSWORD_KEY_LEGACY)
        } catch { /* modo privado */ }
        const savedKey = safeGetSession(SESSION_KEY_BACKUP)
        if (savedKey) {
          try {
            await ensureSalt(user.value.id)
            encryptionKey.value = await importKey(fromBase64(savedKey))
          } catch {
            // Si la restauración falla (p. ej. salt cambiado), limpiar y
            // dejar que el usuario desbloquee manualmente.
            safeRemoveSession(SESSION_KEY_BACKUP)
          }
        }
      }

      // Reaccionar a login/logout desde otras pestañas o refresh de token.
      c.auth.onAuthStateChange((event, session) => {
        user.value = session?.user ?? null
        // Cuando el SDK refresca el JWT (cada ~1h), hay que propagar el
        // nuevo token a Realtime; si no, los canales privados del Y.Doc
        // activo se desconectan silenciosamente al expirar el token viejo.
        if (event === 'TOKEN_REFRESHED' && session?.access_token) {
          c.realtime.setAuth(session.access_token)
        }
        // Tras login: cargar perfil y heartbeat.
        if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && user.value) {
          void loadProfile()
          void touchLastSeen()
        }
        // Al cerrar sesión desde otra pestaña, limpiar la clave local.
        if (event === 'SIGNED_OUT') {
          encryptionKey.value = null
          saltB64.value = null
          profile.value = null
        }
      })
    } finally {
      initializing.value = false
    }
  }

  /**
   * Lee (o crea) el salt PBKDF2 del usuario en `user_settings`. El salt
   * no es secreto — sólo evita que dos usuarios con la misma contraseña
   * deriven la misma clave. Devuelve el salt en base64.
   *
   * IMPORTANTE: si la lectura falla (tabla inexistente, RLS, red), NO
   * generamos un salt efímero. Antes lo hacíamos, lo que producía una
   * CryptoKey que no descifraba nada pero hacía creer al sistema que la
   * sync estaba activa (badge "Sincronizado" mentiroso + pull fallando
   * silenciosamente). Ahora propagamos el error para que `unlock()` falle
   * limpiamente y `isKeyUnlocked` quede en false (badge honesto "Local").
   */
  async function ensureSalt(uid: string): Promise<string> {
    if (saltB64.value) return saltB64.value
    const c = client()
    if (!c) throw new Error('Sync no configurado')

    // Leer la fila existente (basta el salt; es público bajo RLS).
    let row: UserSettingsRow = null
    const { data, error: err } = await c
      .from('user_settings')
      .select('salt, updated_at')
      .eq('user_id', uid)
      .maybeSingle()
    if (err) throw err
    row = data

    if (row?.salt) {
      saltB64.value = row.salt
      return row.salt
    }

    // Primera vez: generar salt. La fila completa la crea pushEncryptedSettings
    // al primer cambio; aquí sólo provisionamos el salt en memoria.
    const fresh = generateSalt()
    const b64 = btoa(String.fromCharCode(...fresh))
    saltB64.value = b64
    return b64
  }

  /** Deriva y guarda en memoria la CryptoKey a partir de la contraseña. */
  async function unlock(password: string): Promise<void> {
    if (!user.value) throw new Error('No hay sesión activa')
    unlocking.value = true
    try {
      const saltB64Val = await ensureSalt(user.value.id)
      const salt = fromBase64(saltB64Val)
      // Derivar exportable para poder respaldar los bytes de la clave (no
      // la contraseña) y restaurarlos tras un F5.
      const key = await deriveKey(password, salt, true)
      encryptionKey.value = key
      try {
        const raw = new Uint8Array(await crypto.subtle.exportKey('raw', key))
        sessionStorage.setItem(SESSION_KEY_BACKUP, toBase64(raw))
      } catch {
        // sessionStorage puede no estar disponible (modo privado); ignorar.
      }
    } finally {
      unlocking.value = false
    }
  }

  /** Descarta la clave de cifrado de memoria (no cierra sesión). */
  function lock() {
    encryptionKey.value = null
    saltB64.value = null
    safeRemoveSession(SESSION_KEY_BACKUP)
  }

  /** Getters/removers de sessionStorage tolerantes a modo privado. */
  function safeGetSession(key: string): string | null {
    try {
      return sessionStorage.getItem(key)
    } catch {
      return null
    }
  }
  function safeRemoveSession(key: string): void {
    try {
      sessionStorage.removeItem(key)
    } catch { /* modo privado */ }
  }

  async function signIn(email: string, password: string) {
    const c = client()
    if (!c) throw new Error('Configura Supabase en Ajustes antes de iniciar sesión.')
    loading.value = true
    error.value = null
    try {
      const { data, error: err } = await c.auth.signInWithPassword({ email, password })
      if (err) throw err
      user.value = data.user
      // Derivar la clave de cifrado ahora que tenemos la contraseña en memoria.
      if (data.user) {
        try {
          await unlock(password)
        } catch {
          // Si la derivación falla (p.ej. tabla inexistente), el login sigue;
          // el usuario podrá reintentar con unlock() más tarde.
        }
        // Cargar perfil (rol/ban) en paralelo; no bloquea el login.
        void loadProfile()
        void touchLastSeen()
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : String(e)
      throw e
    } finally {
      loading.value = false
    }
  }

  async function signUp(email: string, password: string) {
    const c = client()
    if (!c) throw new Error('Configura Supabase en Ajustes antes de registrarte.')
    loading.value = true
    error.value = null
    try {
      const { data, error: err } = await c.auth.signUp({ email, password })
      if (err) throw err
      // signUp puede requerir confirmación por email; en ese caso user queda null.
      user.value = data.user
      if (data.user) {
        try {
          await unlock(password)
        } catch {
          // ver signIn
        }
        // Cargar perfil recién creado por el trigger on_auth_user_created.
        void loadProfile()
      }
    } catch (e) {
      error.value = e instanceof Error ? e.message : String(e)
      throw e
    } finally {
      loading.value = false
    }
  }

  async function signOut() {
    const c = client()
    if (!c) return
    await c.auth.signOut()
    user.value = null
    profile.value = null
    lock()
    resetSupabaseClient()
    // Multitenant: borrar la config IA local (claves/worker) para que no
    // quede en claro en este dispositivo. La nube conserva la copia cifrada
    // ligada a la cuenta; al volver a iniciar sesión se recarga vía pull.
    try {
      const settings = useSettingsStore()
      await settings.clearLocalAi()
    } catch {
      // Si el store no está disponible (p. ej. logout muy temprano), ignorar.
    }
  }

  /**
   * Envía un email de recuperación de contraseña. Supabase devolverá un
   * enlace de redirección a la app; el usuario cambia la contraseña allí.
   * No lanza: en caso de error, lo devuelve para que la UI lo muestre.
   */
  async function resetPassword(email: string): Promise<void> {
    const c = client()
    if (!c) throw new Error('Sync no configurado')
    const { error: err } = await c.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin + '/auth',
    })
    if (err) throw err
  }

  return {
    user,
    userId,
    isAuthenticated,
    initializing,
    loading,
    error,
    encryptionKey,
    saltB64,
    isKeyUnlocked,
    unlocking,
    profile,
    isAdmin,
    isBanned,
    init,
    signIn,
    signUp,
    signOut,
    unlock,
    lock,
    resetPassword,
    loadProfile,
    touchLastSeen,
    updateProfile,
  }
})
